import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import {
  useRole,
  usePermissions,
  useCreateRole,
  useUpdateRole,
} from '../../hooks/useRoles';
import { useSnackbar } from '../../components/SnackbarProvider';
import { usePermissionsContext } from '../../contexts/PermissionsContext';
import { PermissionMatrix } from '../../components/PermissionMatrix';

interface RoleFormProps {
  mode: 'create' | 'edit';
}

export const RoleForm: React.FC<RoleFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showMessage } = useSnackbar();
  const { permissions: userPermissions, isAdmin } = usePermissionsContext();

  const { data: role, isLoading: isLoadingRole } = useRole(id || '');
  const { data: permissions, isLoading: isLoadingPermissions } = usePermissions();
  const createRoleMutation = useCreateRole();
  const updateRoleMutation = useUpdateRole();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [hierarchyLevel, setHierarchyLevel] = useState<number>(3);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ name?: string }>({});

  // Map user's permission keys to permission IDs for the grantable filter
  const grantablePermissionIds = useMemo(() => {
    // Admin can grant all permissions
    if (isAdmin) return undefined;

    // Map permission keys (e.g., "products:read") to their IDs
    if (!permissions || !userPermissions) return [];

    return permissions
      .filter((p) => userPermissions.includes(`${p.resource}:${p.action}`))
      .map((p) => p.id);
  }, [permissions, userPermissions, isAdmin]);

  // Populate form when editing
  useEffect(() => {
    if (mode === 'edit' && role) {
      setName(role.name);
      setDescription(role.description || '');
      setHierarchyLevel(role.hierarchyLevel || 3);
      setSelectedPermissionIds(
        role.rolePermissions?.map((rp) => rp.permissionId) || []
      );
    }
  }, [mode, role]);

  const validateForm = () => {
    const newErrors: { name?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (name.length < 2) {
      newErrors.name = 'Role name must be at least 2 characters';
    } else if (name.length > 50) {
      newErrors.name = 'Role name must not exceed 50 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      hierarchyLevel,
      permissionIds: selectedPermissionIds,
    };

    if (mode === 'create') {
      createRoleMutation.mutate(payload, {
        onSuccess: () => {
          showMessage('Role created successfully', 'success');
          navigate('/app/roles');
        },
        onError: (error: any) => {
          showMessage(
            error.response?.data?.message || 'Failed to create role',
            'error'
          );
        },
      });
    } else {
      updateRoleMutation.mutate(
        { id: id!, data: payload },
        {
          onSuccess: () => {
            showMessage('Role updated successfully', 'success');
            navigate('/app/roles');
          },
          onError: (error: any) => {
            showMessage(
              error.response?.data?.message || 'Failed to update role',
              'error'
            );
          },
        }
      );
    }
  };

  const isLoading = mode === 'edit' && isLoadingRole;
  const isSaving = createRoleMutation.isPending || updateRoleMutation.isPending;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/roles')}
        >
          Back to Roles
        </Button>
      </Box>

      <Typography variant="h4" mb={3}>
        {mode === 'create' ? 'Create New Role' : `Edit Role: ${role?.name}`}
      </Typography>

      {role?.isSystemDefault && mode === 'edit' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This is a system default role. You can edit its permissions, but the role
          itself cannot be deleted.
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Role Details" />
          <CardContent>
            <Box display="flex" flexDirection="column" gap={3}>
              <TextField
                label="Role Name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors({});
                }}
                error={!!errors.name}
                helperText={errors.name}
                required
                fullWidth
                placeholder="e.g., Senior Staff, Trainee, Cashier"
              />
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Brief description of this role's responsibilities..."
              />
              <FormControl fullWidth disabled={role?.isSystemDefault}>
                <InputLabel id="hierarchy-level-label">Hierarchy Level</InputLabel>
                <Select
                  labelId="hierarchy-level-label"
                  value={hierarchyLevel}
                  label="Hierarchy Level"
                  onChange={(e) => setHierarchyLevel(e.target.value as number)}
                >
                  <MenuItem value={1}>Level 1 - Owner (highest privilege)</MenuItem>
                  <MenuItem value={2}>Level 2 - Manager</MenuItem>
                  <MenuItem value={3}>Level 3 - Staff (lowest privilege)</MenuItem>
                </Select>
                <FormHelperText>
                  {role?.isSystemDefault
                    ? 'System default roles have fixed hierarchy levels'
                    : 'Lower level = more privilege. Owners can manage all, Managers can manage Staff only.'}
                </FormHelperText>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Permissions"
            subheader="Select what this role can access and do"
          />
          <Divider />
          <CardContent>
            <PermissionMatrix
              permissions={permissions || []}
              selectedPermissionIds={selectedPermissionIds}
              onChange={setSelectedPermissionIds}
              isLoading={isLoadingPermissions}
              grantablePermissionIds={grantablePermissionIds}
            />

            {selectedPermissionIds.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This role has no permissions selected. Users with this role will
                have very limited access.
              </Alert>
            )}
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={() => navigate('/app/roles')}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={isSaving}
          >
            {isSaving
              ? 'Saving...'
              : mode === 'create'
                ? 'Create Role'
                : 'Save Changes'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export const RoleCreate = () => <RoleForm mode="create" />;
export const RoleEdit = () => <RoleForm mode="edit" />;

export default RoleForm;
